import React from "react";
import { ThumbsUp, AlertCircle, Loader2 } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/Accordion";

const SCORE_COLORS = {
  success: "#6daa45",
  gold:    "#e8af34",
  warning: "#bb653b",
  error:   "#dd6974",
  primary: "#4f98a3",
};

const CONFIDENCE_MAP = {
  high:   { pct: 100, color: SCORE_COLORS.success, label: "High" },
  medium: { pct: 66,  color: SCORE_COLORS.gold,    label: "Medium" },
  low:    { pct: 33,  color: SCORE_COLORS.error,   label: "Low" },
};

const getScoreColor = (score) => {
  const n = parseFloat(score);
  if (n >= 8) return SCORE_COLORS.success;
  if (n >= 6) return SCORE_COLORS.gold;
  if (n >= 4) return SCORE_COLORS.warning;
  return SCORE_COLORS.error;
};

const ScoreRing = ({ arcValue, displayValue, label, arcColor, numberColor }) => {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const normalized = Math.min(Math.max(arcValue, 0), 100);
  const offset = circumference - (normalized / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative flex h-[52px] w-[52px] items-center justify-center">
        <svg width="52" height="52" viewBox="0 0 52 52" className="-rotate-90" aria-hidden="true">
          <circle cx="26" cy="26" r={radius} fill="none" strokeWidth="4"
            stroke="currentColor" className="text-border-primary" />
          <circle cx="26" cy="26" r={radius} fill="none" strokeWidth="4"
            stroke={arcColor} strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.6s ease" }} />
        </svg>
        <span className="absolute text-[15px] font-bold" style={{ lineHeight: 1, color: numberColor }}>
          {displayValue ?? "—"}
        </span>
      </div>
      <p className="text-[11px] font-medium text-text-muted text-center leading-tight">{label}</p>
    </div>
  );
};

const MetaBadge = ({ label, color }) => (
  <span
    className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize"
    style={{ background: `${color}26`, color }}
  >
    {label}
  </span>
);

const OverviewPanelSlot = ({ data, analyzing = false }) => {
  if (!data && !analyzing) return null;

  const analysis   = data?.analysis_data ?? data;
  const score      = analysis?.score       ?? null;
  const fit        = analysis?.overall_fit ?? null;
  const confidence = (analysis?.confidence ?? "").toLowerCase();
  const strengths  = analysis?.top_strengths ?? null;
  const concerns   = analysis?.key_concerns  ?? null;

  const arcValue   = score != null ? parseFloat(score) * 10 : 0;
  const scoreColor = score != null ? getScoreColor(score) : SCORE_COLORS.primary;
  const confConfig = CONFIDENCE_MAP[confidence] ?? CONFIDENCE_MAP["medium"];

  const hasAccordion = strengths || concerns;

  return (
    <div className="flex flex-col px-4 pt-3 pb-4 gap-3">

      {/* ── AI Assessment ─────────────────────────────── */}
      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold uppecase"
         >
         Candidate Overview
        </p>

        <div className="flex justify-center py-0.5">
          <ScoreRing
            arcValue={arcValue}
            displayValue={analyzing ? null : score}
            label="Overall Score"
            arcColor={scoreColor}
            numberColor={scoreColor}
          />
        </div>

        {analyzing ? (
          <div className="mt-2 flex items-center justify-center gap-1.5 text-[#818cf8] text-[11px] font-medium">
            <Loader2 size={12} className="animate-spin" /> Analyzing…
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-center gap-2 flex-wrap">
            {confidence && (
              <MetaBadge label={`${confConfig.label} Confidence`} color={confConfig.color} />
            )}
            {fit && (
              <MetaBadge label={`${fit} Fit`} color={SCORE_COLORS.primary} />
            )}
          </div>
        )}
      </div>

      {/* ── Accordion: Strengths + Concerns ───────────── */}
      {hasAccordion && (
        <Accordion>
          {strengths && (
            <AccordionItem value="strengths">
              <AccordionTrigger>
                <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
                  style={{ color: SCORE_COLORS.success }}>
                  <ThumbsUp className="h-3 w-3 shrink-0" aria-hidden="true" />
                  Top Strengths
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-[12px] leading-[1.7] text-text-primary">{strengths}</p>
              </AccordionContent>
            </AccordionItem>
          )}

          {concerns && (
            <AccordionItem value="concerns">
              <AccordionTrigger>
                <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
                  style={{ color: SCORE_COLORS.warning }}>
                  <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
                  Key Concerns
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-[12px] leading-[1.7] text-text-primary">{concerns}</p>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      )}
    </div>
  );
};

export default OverviewPanelSlot;